<template>
  <!-- 右上の × はスマホだと小さくて押しにくいので出さず、同じ場所に「閉じる」ボタンを置く -->
  <!-- スマホ・タブレットは署名欄まで一度に見えるよう画面いっぱいに出す -->
  <Modal v-bind="$attrs" v-on="$listeners" x-icon-disabled full-screen-on-mobile @closed="closed">
    <template #title>勤務改善指導書 兼 誓約書</template>
    <!--
      「閉じる」は画面の一番上（タイトルの右端）に置き、下の「保存」と押し間違えないようにする
      白地・灰色枠にして保存（紺色）と見た目でも区別する
      保存中に閉じると PDF だけ残って画面に反映されないので、保存中は押せなくする
    -->
    <template #subtitle>
      <div class="flex justify-end">
        <button
          type="button"
          class="inline-flex items-center border border-gray-300 shadow-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          :class="isMobileDevice ? 'px-6 py-2' : 'px-4 py-2 text-sm'"
          :style="isMobileDevice ? { fontSize: '24px' } : {}"
          :disabled="processing"
          @click="close"
        >
          閉じる
        </button>
      </div>
    </template>
    <!-- タイトル横だとモーダルが補足文の長さまで広がるので、タイトルの下に置く -->
    <template #sub-header>
      <span class="font-bold">キーチェーンの指摘がある場合は、従業員に内容を確認のうえ署名していただきます</span>
    </template>

    <!--
      スマホ・タブレットはモーダルが画面いっぱいなので、中身も横いっぱいに広げる
      ＊この画面は PC 幅のまま縮小表示されるため、文字とボタンを全画面表示と同じ大きさにする
      p-2：共通Modalの本文スロットが overflow-y-auto で左右もクリップされるため、
      チェックボックスのフォーカスリング（4px外側まで描かれる）が欠けないように左右の余白を持たせている
    -->
    <div
      class="p-2"
      :class="isMobileDevice ? 'text-2xl leading-relaxed' : 'text-sm'"
      :style="{ width: isMobileDevice ? '100%' : '40rem', maxWidth: isMobileDevice ? '100%' : 'calc(100vw - 6rem)' }"
    >

      <!-- 従業員氏名（指導を受ける人。清掃員のことも管理員のこともある） -->
      <div class="mb-3">
        <div class="font-bold text-gray-700 mb-1" :class="labelClass">従業員氏名（指導を受ける人）</div>
        <input
          type="text"
          v-model="staffName"
          placeholder="指導を受ける人の氏名を入力してください"
          class="w-full rounded text-gray-700 placeholder-red-500"
          :class="[
            !saved && !staffName ? 'border-red-500' : 'border-gray-300',
            isMobileDevice ? 'text-2xl py-3' : 'text-base sm:text-sm'
          ]"
          :disabled="saved"
        />
      </div>

      <!-- 物件名・記入日（誓約書に印字される内容） -->
      <div class="flex flex-wrap mb-3 text-gray-600" :class="labelClass">
        <div class="mr-6">物件名 : <span class="font-bold text-gray-700">{{ siteName }}</span></div>
        <div>記入日 : <span class="font-bold text-gray-700">{{ today }}</span></div>
      </div>

      <!-- 誓約書の内容 -->
      <div class="flex items-center mb-1">
        <div class="font-bold text-gray-700" :class="labelClass">誓約書の内容</div>
        <!--
          文字が小さくて読みにくい方のために、内容だけを画面いっぱいに大きく出す
          ＊スマホ・タブレットは枠の中が全画面表示と同じ大きさなので出さない
        -->
        <button
          v-if="!isMobileDevice"
          type="button"
          class="ml-3 px-3 py-1 rounded bg-indigo-600 text-xs font-bold text-white hover:bg-indigo-700"
          @click="enlarged = true"
        >
          大きく表示
        </button>
      </div>
      <!-- スマホ・タブレットは入れ子のスクロールが扱いにくいので、PC のときだけ高さを抑えて内側でスクロールする -->
      <div
        class="border border-gray-300 rounded bg-gray-50 p-3 mb-3 text-gray-700 leading-relaxed overflow-y-auto"
        :class="isMobileDevice ? 'text-2xl' : 'text-sm sm:max-h-56'"
      >
        <!-- 会社からの指導と、従業員が署名する誓約の2通に分けて見せる -->
        <div v-for="(part, p) in pledgeParts" :key="p" :class="{ 'mt-4': p > 0 }">
          <div class="font-bold text-indigo-700 border-l-4 border-indigo-500 pl-2 mb-2">{{ part.heading }}</div>
          <div v-for="(section, i) in part.sections" :key="i" class="mb-2">
            <div v-if="section.title" class="font-bold mb-1">{{ section.title }}</div>
            <div v-for="(line, j) in section.lines" :key="j">{{ line }}</div>
          </div>
        </div>
      </div>

      <!-- 誓約書の内容の全画面表示 -->
      <div v-if="enlarged" class="fixed inset-0 bg-white flex flex-col" style="z-index: 2000">
        <div class="flex-shrink-0 px-5 py-3 border-b border-gray-300 text-2xl font-bold text-gray-800">
          勤務改善指導書 兼 誓約書
        </div>
        <div class="flex-1 overflow-y-auto px-5 py-4 text-gray-800" style="font-size: 24px; line-height: 1.9">
          <div class="mb-4 text-gray-600">
            <div>物件名 : <span class="font-bold text-gray-800">{{ siteName }}</span></div>
            <div>記入日 : <span class="font-bold text-gray-800">{{ today }}</span></div>
          </div>
          <div v-for="(part, p) in pledgeParts" :key="p" :class="{ 'mt-8': p > 0 }">
            <div class="font-bold text-indigo-700 border-l-4 border-indigo-500 pl-3 mb-3">{{ part.heading }}</div>
            <div v-for="(section, i) in part.sections" :key="i" class="mb-5">
              <div v-if="section.title" class="font-bold mb-1">{{ section.title }}</div>
              <div v-for="(line, j) in section.lines" :key="j" class="mb-1">{{ line }}</div>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 p-4 border-t border-gray-300 text-center">
          <button
            type="button"
            class="w-full max-w-md py-3 rounded bg-indigo-600 text-xl font-bold text-white hover:bg-indigo-700"
            @click="enlarged = false"
          >
            閉じる
          </button>
        </div>
      </div>

      <!-- 確認チェック -->
      <div class="mb-3">
        <label
          class="inline-flex items-center px-2 py-1 rounded border text-gray-700 cursor-pointer"
          :class="!saved && !confirmed ? 'border-red-500' : 'border-transparent'"
        >
          <!-- flex-shrink-0：文言が長くてもチェックボックスが潰れないようにする -->
          <input
            type="checkbox"
            v-model="confirmed"
            class="flex-shrink-0 text-indigo-600 border-gray-300 rounded"
            :class="isMobileDevice ? 'h-8 w-8 mr-3' : 'h-4 w-4 mr-2'"
            :disabled="saved"
          />
          内容を確認しました
        </label>
      </div>

      <!-- 署名欄 -->
      <div>
        <!-- スマホ・タブレットは縮小表示なので、署名欄も線も「書き直す」も大きくする -->
        <SignaturePad
          ref="pad"
          :height="isMobileDevice ? 360 : 180"
          :line-width="isMobileDevice ? 5 : 3"
          :clear-button-size="isMobileDevice ? 'xl' : 'xs'"
          :invalid="!saved && signatureEmpty"
          @change="signatureEmpty = $event"
        >
          <!-- 項目名は「書き直す」と同じ行の左に出す。署名欄は目立たせたいので他の項目名より大きくする -->
          <template #label>
            <div class="font-bold text-gray-700" :class="isMobileDevice ? 'text-3xl' : 'text-base'">従業員（指導を受ける人）の署名</div>
          </template>
        </SignaturePad>
      </div>
    </div>

    <template #action>
      <!-- 下に置くのは「保存」だけ（「閉じる」は一番上、「書き直す」は署名欄の上。近くに別のボタンを置くと押し間違える） -->
      <span
        v-if="!saved && !inputCompleted"
        class="text-red-600 mr-3 self-center"
        :class="isMobileDevice ? 'block text-xl mb-3' : 'text-xs'"
      >
        ※従業員氏名、内容確認チェック、署名を入力してください
      </span>
      <!-- スマホ・タブレットは横いっぱいの大きなボタンにする（PrimaryButton の size より大きくしたいので style で指定） -->
      <PrimaryButton
        :text="processing ? '保存中...' : '保存'"
        size="normal"
        :disabled="!canSave"
        :class="canSave ? '' : 'bg-gray-300'"
        :style="isMobileDevice ? { width: '100%', fontSize: '24px', padding: '1rem 2rem' } : {}"
        @click="save"
      />
    </template>
  </Modal>
</template>

<script>
import Modal from '@components/Modal.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import SignaturePad from '@components/SignaturePad.vue'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import * as utils from '@libs/utils'
import moment from 'moment'

export default {
  components: {
    Modal,
    PrimaryButton,
    SignaturePad
  },

  props: {
    // 巡回予定（id・siteName・staff）
    target: {
      type: Object,
      default: () => ({})
    },
    // モーダルの開閉（親が :open で渡す）
    open: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 従業員氏名（指導を受ける人）
      staffName: '',
      // 内容を確認しました
      confirmed: false,
      // 署名が未入力
      signatureEmpty: true,
      // 保存中
      processing: false,
      // 保存済み
      saved: false,
      // 誓約書の内容を全画面で表示中
      enlarged: false
    }
  },

  computed: {
    /**
     * スマホ・タブレット（Modal の全画面表示と同じ判定）
     */
    isMobileDevice() {
      return utils.deviceInfo.isMobile
    },

    /**
     * 項目名の文字の大きさ
     */
    labelClass() {
      return this.isMobileDevice ? 'text-xl' : 'text-xs'
    },

    /**
     * 物件名
     */
    siteName() {
      return this.target.siteName || ''
    },

    /**
     * 記入日（誓約書に印字される日付）
     */
    today() {
      return moment().format('YYYY/MM/DD')
    },

    /**
     * 誓約書の内容
     * ＊「勤務改善指導書 兼 誓約書」のWordと同じ文面。枠と全画面の両方でこれを出す
     *   ただし「上記、書面にて指導を行いますので…」の一文は画面では出さない（PDF には残す）
     *   原本は「勤務改善指導書（会社→従業員）」と「誓約書（従業員→会社）」の2通なので、
     *   同じ事柄が両側から書かれている。画面でも2通に分けて見せる
     */
    pledgeParts() {
      return [
        {
          heading: '会社からの指導（勤務改善指導書）',
          sections: [
            {
              title: '',
              lines: [
                '貴殿のこれまでの勤務状況に鑑み、下記の事項について指導します。本書の内容を深く理解し、速やかな改善と今後の職務遂行における誠実な対応を求めます。',
                `${this.today}、${this.siteName}での勤務において、以下の「鍵の取り扱いルール」を遵守せずに作業を行っていることを確認しました。`
              ]
            },
            {
              title: '【鍵の取り扱いルール】',
              lines: [
                '・キーボックスから鍵を取り出す際は、その場から移動せず、直ちにキーチェーンへダブルロックを装着すること。',
                '・鍵をキーボックスに戻す際は、キーボックスの前でキーチェーンから外し収納する。'
              ]
            },
            {
              title: '【今後の対応】',
              lines: [
                '今後、同様のルール違反が認められた場合は、就業規則に基づき、物件異動の命、あるいは退職勧告を含む懲戒処分の対象と致します。',
                '万が一、ルールの不備や過失により鍵を紛失し、会社または管理物件に損害を与えた場合は、就業規則の規定に基づき、その損害の一部または全部について賠償を求める場合があります。'
              ]
            }
          ]
        },
        {
          heading: '署名する誓約の内容（誓約書）',
          sections: [
            {
              title: '',
              lines: [
                '私は、上記「勤務改善指導書」の内容について正しく理解し、指導を受けたことを確認いたしました。今後は鍵の取り扱いルールを厳守することを誓約します。',
                '万が一、これに違反した場合には、会社による物件異動や退職勧告を含む処遇に従います。',
                'また、私の不注意やルール違反により鍵を紛失し、会社に多大な損害を与えた場合には、その責任を負い、損害の賠償に応じることを誓います。'
              ]
            }
          ]
        }
      ]
    },

    /**
     * 必須（従業員氏名・内容確認チェック・署名）がすべて入力済みか
     */
    inputCompleted() {
      return !!this.staffName && this.confirmed && !this.signatureEmpty
    },

    /**
     * 保存できるか
     */
    canSave() {
      return !this.processing && !this.saved && this.inputCompleted
    }
  },

  watch: {
    open() {
      if (!this.open) {
        return
      }
      this.initData()
    }
  },

  methods: {
    /**
     * 入力内容の初期化
     */
    initData() {
      this.staffName = ''
      this.confirmed = false
      this.signatureEmpty = true
      this.processing = false
      this.saved = false
      this.enlarged = false

      this.$nextTick(() => {
        if (this.$refs.pad) {
          this.$refs.pad.clear()
          // 非表示のままmountedが走っているため、開いた時点でキャンバスを初期化する
          this.$refs.pad.setup()
        }
      })
    },

    /**
     * 保存
     * ＊バック側でA4のPDFにしてS3へ保存する
     *   （端末の拡大率で見た目が変わらないよう、画像ではなくPDFで保存している）
     */
    async save() {
      if (!this.canSave) {
        return
      }

      this.processing = true
      try {
        const res = await backend.postData('patrolPledge/save', {
          patrolScheduleId: this.target.id,
          siteName: this.siteName,
          staffName: this.staffName,
          signature: this.$refs.pad.toDataUrl()
        })

        if (!res || !res.data || !res.data.path) {
          throw new Error('誓約書の保存に失敗しました')
        }

        this.saved = true
        // 保存先パスは親で項目のdetail1へ保持し、点検項目と一緒に保存する
        this.$emit('saved', res.data)
        // 保存できたらダイアログを閉じる
        this.close()
      } catch (err) {
        await dialogs.showErrorDialog('保存できません', '誓約書の保存に失敗しました。\r\n時間をおいて再度お試しください。')
      } finally {
        this.processing = false
      }
    },

    /**
     * 閉じる
     */
    close() {
      this.$emit('modalClose')
    },

    /**
     * モーダルが閉じられた
     */
    closed() {
      this.$emit('modalClose')
    }
  }
}
</script>
